import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { KpiGrid, StatusChip } from "@/components/kcc/Kpi";
import { Panel, ModuleHeader } from "@/components/kcc/Panel";
import { DataTable, statusToChip } from "@/components/kcc/DataTable";

/**
 * Generic KCC360 workspace page. Reuses enterprise Auth, Data Model, Design System
 * and Notification framework — no duplicate services introduced.
 */
export function WorkspacePage({ kicker, title, description, endpoint, dataKey, columns, accent = "copper", testid, extraKpiAccent }) {
    const [data, setData] = useState(null);
    useEffect(() => { api.get(endpoint).then(({ data }) => setData(data)); }, [endpoint]);
    return (
        <div className="space-y-6" data-testid={testid}>
            <ModuleHeader kicker={kicker} title={title} description={description} />
            <KpiGrid kpis={data?.kpis || []} accent={extraKpiAccent || accent} cols={4} />
            <Panel title={title} accent={accent}>
                <DataTable
                    columns={columns.map((c) => ({
                        ...c,
                        render: c.render || ((r) => {
                            const v = r[c.key];
                            if (c.status) return <StatusChip status={statusToChip(v)}>{v}</StatusChip>;
                            if (c.date) return String(v).slice(0, 10);
                            if (c.currency) return `$${Number(v).toLocaleString()}`;
                            if (typeof v === "number" && c.percent) return `${v}%`;
                            return v ?? "—";
                        }),
                    }))}
                    rows={(data?.[dataKey] || []).slice(0, 50)}
                    testid={`${testid}-table`}
                />
            </Panel>
        </div>
    );
}

export function MineTechnicalServices() {
    return <WorkspacePage
        kicker="Mine Technical Services360" title="Geology · Survey · Drill & Blast · Mine Planning"
        description="Resource definition drilling, grade control, geotechnical, UAV/LiDAR surveys, pit optimisation and scheduling — one governed source of geological and survey truth."
        endpoint="/mts/overview" dataKey="drillholes" accent="copper" testid="mts"
        columns={[
            { key: "hole_id", label: "Hole" },
            { key: "programme", label: "Programme" },
            { key: "depth_m", label: "Depth (m)" },
            { key: "cu_intercept_pct", label: "Cu %" },
            { key: "co_intercept_pct", label: "Co %" },
            { key: "qa_status", label: "QA", status: true },
            { key: "logged_by", label: "Logged by" },
            { key: "date", label: "Date", date: true },
        ]}
    />;
}

export function Laboratory360() {
    return <WorkspacePage
        kicker="Laboratory360 · LIMS" title="Sample Management · Assays · QA/QC · Certificates"
        description="Chain of custody from sample registration to certified assay result. Instrument-integrated, retention-scheduled and audit-ready."
        endpoint="/laboratory/overview" dataKey="samples" accent="cobalt" testid="lab"
        columns={[
            { key: "sample_id", label: "Sample" },
            { key: "matrix", label: "Matrix" },
            { key: "origin", label: "Origin" },
            { key: "priority", label: "Priority", status: true },
            { key: "cu_pct", label: "Cu %" },
            { key: "co_pct", label: "Co %" },
            { key: "assay_method", label: "Method" },
            { key: "turnaround_h", label: "TAT (h)" },
            { key: "qa_status", label: "QA", status: true },
            { key: "certificate_ready", label: "Cert", render: (r) => r.certificate_ready ? <StatusChip status="normal">Ready</StatusChip> : <StatusChip status="warning">Pending</StatusChip> },
        ]}
    />;
}

export function Engineering360() {
    return <WorkspacePage
        kicker="Engineering360" title="Drawings · P&IDs · Standards · Modifications"
        description="Version-controlled engineering documentation, P&IDs, single-line diagrams, data sheets, MoCs and multi-discipline coordination."
        endpoint="/engineering/overview" dataKey="documents" accent="cobalt" testid="engineering"
        columns={[
            { key: "doc_no", label: "Doc #" },
            { key: "title", label: "Title" },
            { key: "type", label: "Type" },
            { key: "discipline", label: "Discipline" },
            { key: "area", label: "Area" },
            { key: "revision", label: "Rev" },
            { key: "status", label: "Status", status: true },
            { key: "next_review", label: "Next Review", date: true },
        ]}
    />;
}

export function Projects360() {
    return <WorkspacePage
        kicker="Projects360" title="Capital Projects · Turnarounds · Portfolio"
        description="Stage-gate governance, earned-value analysis, CPI/SPI, portfolio prioritisation and benefits realisation."
        endpoint="/projects/overview" dataKey="projects" accent="purple" testid="projects"
        columns={[
            { key: "name", label: "Project" },
            { key: "type", label: "Type" },
            { key: "stage", label: "Stage" },
            { key: "budget_musd", label: "Budget $M" },
            { key: "spent_musd", label: "Spent $M" },
            { key: "progress_pct", label: "Progress", render: (r) => <div><div className="text-white text-xs">{r.progress_pct}%</div><div className="w-24 h-1.5 bg-white/10 rounded"><div className="h-1.5 rounded bg-gradient-to-r from-kcc-copper to-kcc-cobalt-light" style={{ width: `${r.progress_pct}%` }} /></div></div> },
            { key: "cpi", label: "CPI" },
            { key: "spi", label: "SPI" },
            { key: "risk", label: "Risk", status: true },
            { key: "target_completion", label: "Target" },
        ]}
    />;
}

export function Logistics360() {
    return <WorkspacePage
        kicker="Logistics360" title="Product Dispatch · Rail · Ports · Exports"
        description="Copper cathode and cobalt hydroxide dispatch from Kolwezi to Dar es Salaam / Durban / Beira / Lobito ports — with customs, insurance and ETA prediction."
        endpoint="/logistics/overview" dataKey="shipments" accent="copper" testid="logistics"
        columns={[
            { key: "shipment", label: "Shipment" },
            { key: "product", label: "Product" },
            { key: "tonnes", label: "Tonnes" },
            { key: "mode", label: "Mode" },
            { key: "carrier", label: "Carrier" },
            { key: "destination", label: "Destination" },
            { key: "eta", label: "ETA", date: true },
            { key: "customs_docs", label: "Customs", status: true },
            { key: "status", label: "Status", status: true },
        ]}
    />;
}

export function Community360() {
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/community/overview").then(({ data }) => setData(data)); }, []);
    return (
        <div className="space-y-6">
            <ModuleHeader kicker="Community360" title="Stakeholder Engagement · Grievances · Social Investment"
                description="Community stakeholder register, engagement history, grievance workflows, CSR investment tracking and sentiment monitoring." />
            <KpiGrid kpis={data?.kpis || []} accent="green" cols={4} />
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <Panel title="Stakeholder Register" accent="green">
                    <DataTable
                        columns={[
                            { key: "name", label: "Stakeholder" },
                            { key: "type", label: "Type" },
                            { key: "priority", label: "Priority", render: (r) => <StatusChip status={statusToChip(r.priority)}>{r.priority}</StatusChip> },
                            { key: "engagements_ytd", label: "Engagements" },
                            { key: "open_grievances", label: "Open Griev." },
                            { key: "sentiment", label: "Sentiment", render: (r) => <StatusChip status={statusToChip(r.sentiment)}>{r.sentiment}</StatusChip> },
                        ]}
                        rows={data?.stakeholders || []}
                    />
                </Panel>
                <Panel title="Grievance Register" accent="amber">
                    <DataTable
                        columns={[
                            { key: "ref", label: "Ref" },
                            { key: "type", label: "Type" },
                            { key: "community", label: "Community" },
                            { key: "raised", label: "Raised", render: (r) => r.raised.slice(0, 10) },
                            { key: "sla_days", label: "SLA (d)", render: (r) => <span className={r.sla_days < 0 ? "text-kcc-red" : r.sla_days < 7 ? "text-kcc-amber" : "kcc-text-silver"}>{r.sla_days}</span> },
                            { key: "status", label: "Status", render: (r) => <StatusChip status={statusToChip(r.status)}>{r.status}</StatusChip> },
                        ]}
                        rows={data?.grievances || []}
                    />
                </Panel>
            </div>
        </div>
    );
}

export function ESG360() {
    return <WorkspacePage
        kicker="ESG360" title="Sustainability Metrics · Carbon · Water · Governance"
        description="Independently assured ESG metrics across environment, social and governance. Aligned with international reporting standards."
        endpoint="/esg/overview" dataKey="metrics" accent="green" testid="esg"
        columns={[
            { key: "metric", label: "Metric" },
            { key: "actual", label: "Actual" },
            { key: "unit", label: "Unit" },
            { key: "previous_year", label: "Prev Year" },
            { key: "target", label: "Target" },
            { key: "trend", label: "Trend", render: (r) => <span className={r.trend === "up" ? "text-kcc-green" : "text-kcc-red"}>{r.trend === "up" ? "▲ up" : "▼ down"}</span> },
            { key: "assurance", label: "Assurance", status: true },
        ]}
    />;
}

export function Energy360() {
    return <WorkspacePage
        kicker="Energy360" title="Power · Substations · Transformers · Renewables"
        description="Site energy infrastructure with real-time load, power factor, temperature, energy efficiency and renewables integration."
        endpoint="/energy/overview" dataKey="infrastructure" accent="amber" testid="energy"
        columns={[
            { key: "tag", label: "Tag" },
            { key: "name", label: "Asset" },
            { key: "type", label: "Type" },
            { key: "capacity_mva", label: "MVA" },
            { key: "load_pct", label: "Load %" },
            { key: "power_factor", label: "PF" },
            { key: "temperature_c", label: "Temp °C" },
            { key: "status", label: "Status", status: true },
        ]}
    />;
}

export function Risk360() {
    return <WorkspacePage
        kicker="Risk360 · Enterprise Risk" title="Strategic · Operational · Financial · Cyber · Safety · Environmental"
        description="Enterprise risk register with likelihood, impact, residual score, mitigation, control owner and Board escalation."
        endpoint="/risk/overview" dataKey="risks" accent="red" testid="risk"
        columns={[
            { key: "category", label: "Category" },
            { key: "risk", label: "Risk" },
            { key: "likelihood", label: "Lik" },
            { key: "impact", label: "Imp" },
            { key: "residual_score", label: "Score", render: (r) => <span className={r.residual_score >= 15 ? "text-kcc-red font-mono-kcc" : r.residual_score >= 9 ? "text-kcc-amber font-mono-kcc" : "font-mono-kcc kcc-text-silver"}>{r.residual_score}</span> },
            { key: "mitigation", label: "Mitigation" },
            { key: "owner", label: "Owner" },
            { key: "status", label: "Status", status: true },
        ]}
    />;
}

export function Legal360() {
    return <WorkspacePage
        kicker="Legal360" title="Contracts · Claims · Litigation · Compliance"
        description="Legal matters register with external counsel, risk exposure, statutory obligations and Board reporting."
        endpoint="/legal/overview" dataKey="matters" accent="purple" testid="legal"
        columns={[
            { key: "matter_no", label: "Matter" },
            { key: "type", label: "Type" },
            { key: "counterparty", label: "Counterparty" },
            { key: "external_counsel", label: "Counsel" },
            { key: "risk_exposure_usd", label: "Exposure", currency: true },
            { key: "next_action", label: "Next Action", date: true },
            { key: "status", label: "Status", status: true },
        ]}
    />;
}
